export interface ConversationDraftEntry {
  text: string;
  editedAt: number;
  committedAt: number | null;
}

export interface ConversationDraftStore {
  getDraft(conversationId: number): string | undefined;
  getEntry(conversationId: number): ConversationDraftEntry | undefined;
  setDraft(conversationId: number, text: string, editedAt?: number): void;
  commitDraft(conversationId: number): void;
  clearDraft(conversationId: number): void;
  getSnapshot(): ReadonlyMap<number, ConversationDraftEntry>;
  subscribe(listener: () => void): () => void;
}

/**
 * Per-Inbox, in-memory drafts. Create one store per organization lifetime.
 * Not a module singleton — org switches and tests must not share maps.
 */
export function createConversationDraftStore(): ConversationDraftStore {
  const drafts = new Map<number, ConversationDraftEntry>();
  let snapshot: ReadonlyMap<number, ConversationDraftEntry> = new Map();
  const listeners = new Set<() => void>();

  function emit(): void {
    snapshot = new Map(drafts);
    for (const listener of listeners) {
      listener();
    }
  }

  return {
    getDraft(conversationId) {
      return drafts.get(conversationId)?.text;
    },

    getEntry(conversationId) {
      return drafts.get(conversationId);
    },

    setDraft(conversationId, text, editedAt = Date.now()) {
      if (text.trim().length === 0) {
        if (!drafts.has(conversationId)) {
          return;
        }
        drafts.delete(conversationId);
        emit();
        return;
      }
      const previous = drafts.get(conversationId);
      if (previous != null && previous.text === text) {
        return;
      }
      drafts.set(conversationId, {
        text,
        editedAt,
        committedAt: previous?.committedAt ?? null,
      });
      emit();
    },

    commitDraft(conversationId) {
      const previous = drafts.get(conversationId);
      if (previous == null || previous.text.trim().length === 0) {
        if (previous != null) {
          drafts.delete(conversationId);
          emit();
        }
        return;
      }
      if (previous.committedAt === previous.editedAt) {
        return;
      }
      drafts.set(conversationId, {
        ...previous,
        committedAt: previous.editedAt,
      });
      emit();
    },

    clearDraft(conversationId) {
      if (!drafts.has(conversationId)) {
        return;
      }
      drafts.delete(conversationId);
      emit();
    },

    getSnapshot() {
      return snapshot;
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

export function commitConversationDraftOnLeave(
  store: ConversationDraftStore,
  previousConversationId: number | null,
  nextConversationId: number,
): void {
  if (previousConversationId == null || previousConversationId === nextConversationId) {
    return;
  }
  store.commitDraft(previousConversationId);
}

export function applyConversationDraftSendResult(
  store: ConversationDraftStore,
  conversationId: number,
  success: boolean,
): void {
  if (success) {
    store.clearDraft(conversationId);
  }
}
